import type { Mention } from '../../data/types';

export type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'mention'; text: string; mention: Mention }
  | { kind: 'pagebreak'; page: number | undefined };

/**
 * Divide el texto de un párrafo en tramos de texto, menciones y saltos de página. Un párrafo de
 * Quadrado puede cruzar dos o tres páginas impresas, así que admite varios saltos.
 * Los offsets de las menciones se refieren al campo `text` publicado. Una mención cuyo
 * tramo no coincide con su `surfaceForm` se ignora (el texto cambió tras anotar) y se
 * informa por consola: nunca se resalta un lugar sobre un texto distinto.
 */
export function splitParagraph(
  text: string,
  mentions: Mention[],
  pageBreak?: { offset: number; page: number | undefined } | Array<{ offset: number; page: number | undefined }>,
): Segment[] {
  const valid = mentions
    .filter((m) => {
      const ok = m.charStart >= 0 && m.charEnd <= text.length && text.slice(m.charStart, m.charEnd) === m.surfaceForm;
      if (!ok) console.warn(`Mención desplazada ignorada: ${m.id}`);
      return ok;
    })
    .sort((a, b) => a.charStart - b.charStart)
    .filter((m, i, arr) => i === 0 || m.charStart >= arr[i - 1].charEnd); // sin solapes

  const cuts: Array<{ at: number; seg: Segment; end: number }> = valid.map((m) => ({ at: m.charStart, end: m.charEnd, seg: { kind: 'mention', text: m.surfaceForm, mention: m } }));
  const saltos = pageBreak ? (Array.isArray(pageBreak) ? pageBreak : [pageBreak]) : [];
  for (const salto of saltos) {
    if (!(salto.offset > 0 && salto.offset < text.length)) continue;
    // no partir una mención: si el salto cae dentro, se desplaza al final de la mención
    const inside = valid.find((m) => salto.offset > m.charStart && salto.offset < m.charEnd);
    const at = inside ? inside.charEnd : salto.offset;
    cuts.push({ at, end: at, seg: { kind: 'pagebreak', page: salto.page } });
  }
  cuts.sort((a, b) => a.at - b.at || (a.seg.kind === 'pagebreak' ? 1 : -1));

  const out: Segment[] = [];
  let pos = 0;
  for (const c of cuts) {
    if (c.at > pos) out.push({ kind: 'text', text: text.slice(pos, c.at) });
    out.push(c.seg);
    pos = Math.max(pos, c.end);
  }
  if (pos < text.length) out.push({ kind: 'text', text: text.slice(pos) });
  return out;
}

/** Clave de orden de lectura a partir del ID estable: capítulo, párrafo, nota, posición. */
export function readingKey(m: Pick<Mention, 'paragraphId' | 'charStart'>): [number, number, number, number] {
  const r = /^seg-c(\d+)(?:-s\d+)?-p(\d+)(?:-n(\d+))?$/.exec(m.paragraphId);
  if (!r) return [999, 999, 999, m.charStart];
  return [Number(r[1]), Number(r[2]), r[3] ? Number(r[3]) : 0, m.charStart];
}

export function byReadingOrder<T extends Pick<Mention, 'paragraphId' | 'charStart'>>(a: T, b: T): number {
  const ka = readingKey(a);
  const kb = readingKey(b);
  for (let i = 0; i < 4; i++) if (ka[i] !== kb[i]) return ka[i] - kb[i];
  return 0;
}

export const chapterOf = (paragraphId: string): string => paragraphId.replace(/^((?:sal|avi|seg)-c\d+).*$/, '$1');

const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
/** Número romano del capítulo al que pertenece un párrafo, sea de la parte que sea. */
export const chapterRoman = (paragraphId: string): string => ROMAN[Number(paragraphId.match(/-c(\d{2})/)?.[1])] ?? '?';
/** Párrafo de cuerpo que contiene una nota (seg-c05-p017-n1 → seg-c05-p017). */
export const hostParagraph = (paragraphId: string): string => paragraphId.replace(/-n\d+$/, '');
